import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Fab, IconButton, Tooltip } from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import FavoriteIcon from '@mui/icons-material/Favorite';
import AuthData from 'lib/authData';
import { CARES_SUPPORT_WIDGET_ENABLED, getFeature } from 'components/common/FeatureProvider';
import rolesList, { isOsUser } from 'lib/builders/rolesList';
import trackCustomEvent, { GTMEvent } from 'lib/gtm/customEvent';
import { getEnv } from 'lib/helpers/env';

// No personal data may ever be appended to this URL (legal review scope guard).
const DEFAULT_CARES_URL = 'https://cares.tagsupport.co.uk/?utm_source=cms&utm_medium=widget&utm_campaign=cares-support';
// Allow per-environment override via the runtime env var (window._env_), falling
// back to the default when it is not provided. See create_runtime_env_vars.sh.
export const CARES_URL = getEnv('CARES_SUPPORT_WIDGET_URL', DEFAULT_CARES_URL);

export const POSITION_STORAGE_KEY = 'cares-widget-pos';
export const COLLAPSED_STORAGE_KEY = 'cares-widget-collapsed';

const WIDGET_SIZE = 62;
const EDGE_MARGIN = 8;
// pointer movement below this many pixels is a click, not a drag
const DRAG_THRESHOLD = 5;
const TUCK_OFFSET = 48;
// the minimise chip only appears when the widget is parked this close to a
// horizontal viewport edge — tucking from anywhere else makes no sense
const NEAR_EDGE_THRESHOLD = 24;

// The ticket's "internal staff" set is narrower than isInternalUser():
// system admin ('admin') and 'os-data-officer' are explicitly excluded.
export const isCaresEligible = (role?: string): boolean => Boolean(role) && isOsUser(role) && role !== rolesList.OS_DATA_OFFICER;

type Position = { x: number; y: number };

const readStoredPosition = (): Position | null => {
  try {
    const raw = localStorage.getItem(POSITION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') {
      return parsed;
    }
  } catch {
    // corrupted or inaccessible storage — fall back to the default corner
  }
  return null;
};

export default function CaresWidget() {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<Position | null>(readStoredPosition);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSED_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [dragging, setDragging] = useState(false);
  // set when minimising while the pointer is still over the widget — keeps
  // the hover slide-out from cancelling the tuck until the pointer leaves
  const [hoverHold, setHoverHold] = useState(false);
  const dragState = useRef({ startX: 0, startY: 0, originX: 0, originY: 0, active: false, dragged: false });
  // during a drag we move a lightweight ghost via direct DOM writes and leave
  // the real widget untouched — see handlePointerMove. dragPos holds the latest
  // ghost position so pointer-up can commit it to state in a single render.
  const ghostRef = useRef<HTMLDivElement>(null);
  const dragPosRef = useRef<Position | null>(null);
  const suppressClick = useRef(false);
  const suppressTimer = useRef<ReturnType<typeof setTimeout>>();
  const collapsedRef = useRef(collapsed);
  collapsedRef.current = collapsed;
  const lastViewport = useRef({ w: window.innerWidth, h: window.innerHeight });

  const widgetWidth = () => wrapRef.current?.getBoundingClientRect().width || WIDGET_SIZE;
  const widgetHeight = () => wrapRef.current?.getBoundingClientRect().height || WIDGET_SIZE;

  const clampToViewport = (pos: Position): Position => ({
    x: Math.max(EDGE_MARGIN, Math.min(pos.x, window.innerWidth - widgetWidth() - EDGE_MARGIN)),
    y: Math.max(EDGE_MARGIN, Math.min(pos.y, window.innerHeight - widgetHeight() - EDGE_MARGIN)),
  });

  const nearestEdge = (pos: Position): 'left' | 'right' => (pos.x + widgetWidth() / 2 < window.innerWidth / 2 ? 'left' : 'right');

  const snapToNearestEdge = (pos: Position): Position =>
    clampToViewport({
      x: nearestEdge(pos) === 'left' ? EDGE_MARGIN : window.innerWidth - widgetWidth() - EDGE_MARGIN,
      y: pos.y,
    });

  const savePosition = (pos: Position) => {
    try {
      localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(pos));
    } catch {
      // storage unavailable (private mode / quota) — position just won't persist
    }
  };

  const saveCollapsed = (value: boolean) => {
    try {
      localStorage.setItem(COLLAPSED_STORAGE_KEY, String(value));
    } catch {
      // storage unavailable — the choice just won't persist
    }
  };

  // a stored position must never end up off-screen after a viewport change;
  // each axis stays anchored to its nearest edge as the window resizes
  useEffect(() => {
    // a position restored from a larger screen may start out of bounds
    setPosition((prev) => {
      if (!prev) return prev;
      const c = clampToViewport(prev);
      return c.x === prev.x && c.y === prev.y ? prev : c;
    });
    const onResize = () => {
      const { w: prevW, h: prevH } = lastViewport.current;
      lastViewport.current = { w: window.innerWidth, h: window.innerHeight };
      setPosition((prev) => {
        if (!prev) return prev;
        // an axis in the far half of the old viewport is anchored to that
        // far edge — shift it by the viewport delta to keep its edge offset
        const anchoredRight = prev.x + widgetWidth() / 2 >= prevW / 2;
        const anchoredBottom = prev.y + widgetHeight() / 2 >= prevH / 2;
        const moved = {
          x: anchoredRight ? prev.x + (window.innerWidth - prevW) : prev.x,
          y: anchoredBottom ? prev.y + (window.innerHeight - prevH) : prev.y,
        };
        const next = collapsedRef.current ? snapToNearestEdge(moved) : clampToViewport(moved);
        savePosition(next);
        return next;
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // empty deps is safe: the helpers used above close over refs and window
    // only, never state or props — re-binding on re-render would gain nothing
  }, []);

  // a drag-end timeout must not fire into an unmounted component
  useEffect(() => () => clearTimeout(suppressTimer.current), []);

  // a gesture in progress must not leak its window listeners if we unmount
  useEffect(() => () => dragCleanup.current?.(), []);

  // Civic Cookie Control injects its banner (#ccc) into <body> at the max
  // 32-bit z-index — the same ceiling we use — but it loads asynchronously via
  // GTM, so it lands *after* #root in the DOM and wins the equal-z-index tie by
  // source order. The only way to sit on top is to be later in the DOM than it,
  // so we render into our own <body>-level node and keep that node the last
  // child of <body>; a MutationObserver re-appends it whenever the banner (or
  // any other late overlay) inserts itself afterwards.
  const [portalEl] = useState(() => document.createElement('div'));
  useEffect(() => {
    portalEl.setAttribute('data-cares-widget-portal', '');
    document.body.appendChild(portalEl);
    const keepLast = () => {
      // Leave our node where it is mid-drag. The gesture is tracked on window so
      // it no longer depends on this node staying put, but re-appending it would
      // still re-mount the (hidden) widget for nothing. We re-assert the
      // last-child slot once, on drop, instead (see endDrag).
      if (dragState.current.dragged) return;
      // appendChild moves an existing node; this fires one more (no-op) mutation
      // and then settles — there is no re-entrant loop while we are already last
      if (document.body.lastElementChild !== portalEl) {
        document.body.appendChild(portalEl);
      }
    };
    const observer = new MutationObserver(keepLast);
    observer.observe(document.body, { childList: true });
    return () => {
      observer.disconnect();
      portalEl.remove();
    };
  }, [portalEl]);

  // Holds the window-listener teardown for the active gesture. We deliberately
  // do NOT use setPointerCapture: capture is bound to one DOM node, and on this
  // page that node lives inside a body portal that MUI poppers/tooltips and the
  // cookie banner constantly reshuffle — any reshuffle silently drops the capture
  // and "drops" the drag mid-move. window listeners can't be detached by DOM
  // churn, so the gesture survives anything that happens to the node.
  const dragCleanup = useRef<() => void>();

  const moveDrag = (clientX: number, clientY: number) => {
    const s = dragState.current;
    if (!s.active) return;
    const dx = clientX - s.startX;
    const dy = clientY - s.startY;
    if (!s.dragged && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
    if (!s.dragged) {
      s.dragged = true;
      suppressClick.current = true;
      setDragging(true);
      if (collapsedRef.current) {
        // dragging means the user is picking a new spot — un-tuck first
        collapsedRef.current = false;
        setCollapsed(false);
        saveCollapsed(false);
        setHoverHold(false);
      }
    }
    const next = clampToViewport({ x: s.originX + dx, y: s.originY + dy });
    dragPosRef.current = next;
    // Move the ghost only — never the real widget. setState on every move would
    // re-render the whole heavy widget (gradient Fab + tooltip popper) ~60×/sec.
    // A direct transform write keeps the drag on the compositor. The committed
    // position lands once, on drop.
    if (ghostRef.current) {
      ghostRef.current.style.transform = `translate3d(${next.x}px, ${next.y}px, 0)`;
    }
  };

  const endDrag = () => {
    const s = dragState.current;
    s.active = false;
    dragCleanup.current?.();
    dragCleanup.current = undefined;
    if (!s.dragged) return;
    s.dragged = false;
    setDragging(false);
    // The keep-last observer was paused for the whole drag; now it's done it's
    // safe to re-assert our last-child slot, in case a tooltip/banner pushed us
    // down while we were paused.
    if (document.body.lastElementChild !== portalEl) {
      document.body.appendChild(portalEl);
    }
    const dropped = dragPosRef.current;
    dragPosRef.current = null;
    setPosition((prev) => {
      // the ghost carried the live position during the drag; commit it now
      const base = dropped ?? prev;
      if (!base) return prev;
      const next = collapsedRef.current ? snapToNearestEdge(base) : base;
      savePosition(next);
      return next;
    });
    // the click that follows this pointerup is suppressed; re-arm afterwards
    // in case it lands outside the anchor and never resets the flag itself
    suppressTimer.current = setTimeout(() => {
      suppressClick.current = false;
    }, 0);
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('.CaresWidget-chip')) return;
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      originX: rect.left,
      originY: rect.top,
      active: true,
      dragged: false,
    };
    // Run the rest of the gesture off window so nothing in the DOM can drop it.
    const onMove = (ev: PointerEvent) => moveDrag(ev.clientX, ev.clientY);
    const onUp = () => endDrag();
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    dragCleanup.current = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  };

  const toggleCollapsed = (e: React.MouseEvent<HTMLButtonElement>) => {
    const next = !collapsed;
    setCollapsed(next);
    saveCollapsed(next);
    if (next) {
      // tuck immediately even though the pointer is still over the widget;
      // hover slide-out re-arms once the pointer leaves
      setHoverHold(true);
      e.currentTarget.blur();
      const rect = wrapRef.current?.getBoundingClientRect();
      const current = position ?? (rect ? { x: rect.left, y: rect.top } : null);
      if (current) {
        const snapped = snapToNearestEdge(current);
        setPosition(snapped);
        savePosition(snapped);
      }
    }
  };

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (suppressClick.current) {
      e.preventDefault();
      suppressClick.current = false;
      return;
    }
    // deliberately no userId/role here — amplitudeIdentify already attaches
    // session-level segmentation (role/companyId, no raw userId) once per
    // session, and this feature's legal review is zero-PII
    trackCustomEvent({
      event: GTMEvent.CARES_WIDGET_CLICKED,
      category: 'cares',
      action: 'click',
      label: 'cares_support_widget',
    });
  };

  const currentUser = AuthData.get()?.user;
  if (!getFeature(CARES_SUPPORT_WIDGET_ENABLED) || !isCaresEligible(currentUser?.role)) {
    return null;
  }

  const side = position ? nearestEdge(position) : 'left';
  const tuckTransform = collapsed && !dragging ? `translateX(${side === 'right' ? TUCK_OFFSET : -TUCK_OFFSET}px)` : 'none';
  // the default corner hugs the left edge (left: 20), so the chip shows there
  const gapLeft = position ? position.x : 20;
  const gapRight = position ? window.innerWidth - position.x - widgetWidth() : Infinity;
  const nearEdge = Math.min(gapLeft, gapRight) <= NEAR_EDGE_THRESHOLD;

  return createPortal(
    <Box
      ref={wrapRef}
      data-testid="cares-widget-wrap"
      onPointerDown={handlePointerDown}
      onPointerLeave={() => setHoverHold(false)}
      sx={{
        position: 'fixed',
        // stay above SideNav (1500) and MUI modals (~1300). We match Civic's
        // max 32-bit z-index rather than exceed it (browsers clamp here) and
        // win the tie against its banner via DOM source order — see the
        // body-portal MutationObserver above. QA finding: widget got stuck
        // under the cookie banner.
        zIndex: 2147483647,
        ...(position ? { left: `${position.x}px`, top: `${position.y}px` } : { left: '20px', bottom: '104px' }),
        cursor: dragging ? 'grabbing' : 'grab',
        touchAction: 'none',
        // the wrapper (= the hover zone) never moves — only the inner element
        // tucks. A hover zone that moves with the tuck flickers in a loop when
        // the cursor sits in a strip only one of the two positions covers.
        ...(collapsed
          ? {
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                bottom: 0,
                width: `${TUCK_OFFSET}px`,
                ...(side === 'left' ? { left: `-${TUCK_OFFSET}px` } : { right: `-${TUCK_OFFSET}px` }),
              },
            }
          : {}),
        ...(hoverHold ? {} : { '&:hover .CaresWidget-inner, &:focus-within .CaresWidget-inner': { transform: 'none' } }),
        '@media print': { display: 'none' },
        '& .CaresWidget-chip': {
          opacity: 0,
          transform: 'scale(0.5)',
          pointerEvents: 'none',
          transition: 'opacity 180ms ease, transform 180ms cubic-bezier(0.34, 1.56, 0.64, 1)',
        },
        '&:hover .CaresWidget-chip, &:focus-within .CaresWidget-chip': {
          opacity: 1,
          transform: 'scale(1)',
          pointerEvents: 'auto',
        },
      }}
    >
      <Box
        className="CaresWidget-inner"
        sx={{
          position: 'relative',
          transform: tuckTransform,
          transition: dragging ? 'none' : 'transform 280ms cubic-bezier(0.4, 0, 0.2, 1)',
          // hidden while dragging — the ghost stands in for it and follows the
          // cursor; this leaves the real widget parked and re-render-free
          opacity: dragging ? 0 : 1,
        }}
      >
        {/* disablePortal keeps the popper inside the moving inner box, so the
          tooltip travels with the widget while it tucks, peeks, or drags
          instead of staying anchored where the hover started */}
        <Tooltip
          title="I need a moment"
          disableInteractive
          placement={side === 'left' ? 'right' : 'left'}
          PopperProps={{ disablePortal: true }}
          componentsProps={{
            // the in-wrapper popper is width-constrained by the 62px widget —
            // without nowrap the label wraps to one word per line
            tooltip: { sx: { whiteSpace: 'nowrap', maxWidth: 'none' } },
          }}
        >
          <Fab
            component="a"
            href={CARES_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="I need a moment"
            data-testid="cares-widget"
            draggable={false}
            onClick={handleLinkClick}
            sx={{
              width: WIDGET_SIZE,
              height: WIDGET_SIZE,
              border: '3px solid #fff',
              color: '#fff',
              // exact pink pending design-token confirmation (plan §13)
              background: 'radial-gradient(circle at 34% 28%, #F790B5, #EC5C90 55%, #E2447E)',
              boxShadow: '0 10px 24px rgba(226, 68, 126, 0.35)',
              '&:hover': {
                background: 'radial-gradient(circle at 34% 28%, #F790B5, #EC5C90 55%, #E2447E)',
                boxShadow: '0 14px 30px rgba(226, 68, 126, 0.45)',
              },
            }}
          >
            <FavoriteIcon />
          </Fab>
        </Tooltip>
        {nearEdge && (
          <IconButton
            className="CaresWidget-chip"
            size="small"
            aria-label={collapsed ? 'Restore widget' : 'Minimise widget'}
            onClick={toggleCollapsed}
            sx={{
              position: 'absolute',
              top: '-7px',
              ...(side === 'left' ? { right: '-7px' } : { left: '-7px' }),
              width: 24,
              height: 24,
              backgroundColor: 'rgba(255, 255, 255, 0.94)',
              backdropFilter: 'blur(6px)',
              boxShadow: '0 2px 10px rgba(34, 27, 23, 0.22)',
              '&:hover': { backgroundColor: '#fff', color: '#DC2040' },
            }}
          >
            {collapsed ? <AddRoundedIcon sx={{ fontSize: 14 }} /> : <RemoveRoundedIcon sx={{ fontSize: 14 }} />}
          </IconButton>
        )}
      </Box>
      {dragging && (
        // Lightweight drag proxy: no Fab ripple, no tooltip popper, no
        // transition — just a circle we shove around with a transform. Lives
        // outside the faded inner box so it stays fully opaque, and is moved by
        // a direct ref write in handlePointerMove (never via React state).
        <Box
          ref={ghostRef}
          aria-hidden
          data-testid="cares-widget-ghost"
          sx={{
            position: 'fixed',
            left: 0,
            top: 0,
            transform: `translate3d(${dragPosRef.current?.x ?? 0}px, ${dragPosRef.current?.y ?? 0}px, 0)`,
            width: WIDGET_SIZE,
            height: WIDGET_SIZE,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            border: '3px solid #fff',
            color: '#fff',
            background: 'radial-gradient(circle at 34% 28%, #F790B5, #EC5C90 55%, #E2447E)',
            boxShadow: '0 14px 30px rgba(226, 68, 126, 0.45)',
            opacity: 0.9,
            pointerEvents: 'none',
            willChange: 'transform',
            '@media print': { display: 'none' },
          }}
        >
          <FavoriteIcon />
        </Box>
      )}
    </Box>,
    portalEl,
  );
}
